import type { TripListItem } from "@/lib/bookings-api";

/**
 * Task #5765 — the ONE consistent renter-facing outcome, resolved for the Trips
 * cards. This is the frontend twin of the api-server `renter-outcome` model:
 * it collapses every later-stage terminal a renter can land in onto a single
 * plain-language subline key + a next-action, so the Trips surface never
 * disagrees with the durable notification / email.
 *
 * Copy is keyed off the server-projected canonical `TripListItem.renter_outcome`
 * (sourced from the stamped durable notification for payment-side terminals
 * that keep `status='approved'` — approve capture-fail, pay-later charge-fail —
 * or from a host-driven terminal status). It is NOT re-inferred from `status`
 * except as a fallback for a genuine `cancelled` and for older API responses
 * that predate the `renter_outcome` projection.
 *
 * `kind` lets the two render sites style themselves: a `terminal` outcome is a
 * neutral note in the cancelled/expired section; a `payment_action` outcome is a
 * still-actionable failure that surfaces as an inline alert on the upcoming card
 * (those trips keep `status='approved'` so they list as upcoming).
 *
 * Returns i18n KEYS (not resolved copy) so this stays pure and unit-testable;
 * the component maps them through `t()`.
 */
export type RenterOutcomeCardKind = "terminal" | "payment_action";

export interface RenterOutcomeCard {
  sublineKey: string;
  ctaKey: string;
  href: string;
  kind: RenterOutcomeCardKind;
}

export function resolveRenterOutcomeCard(
  trip: Pick<TripListItem, "renter_outcome" | "status" | "public_id">,
): RenterOutcomeCard | null {
  switch (trip.renter_outcome) {
    case "host_declined":
      return { sublineKey: "booking.sl_declined", ctaKey: "booking.cta_find_another_car", href: "/search", kind: "terminal" };
    case "expired":
      return { sublineKey: "booking.sl_expired", ctaKey: "booking.cta_find_another_car", href: "/search", kind: "terminal" };
    case "hold_released_rebook":
      return { sublineKey: "booking.sl_hold_released", ctaKey: "booking.cta_find_another_car", href: "/search", kind: "payment_action" };
    case "card_declined":
    case "payment_incomplete":
    case "scheduled_charge_failed":
      return { sublineKey: "booking.sl_payment_failed", ctaKey: "booking.cta_retry_payment", href: `/trips/${trip.public_id}`, kind: "payment_action" };
  }
  // Fallback for genuine cancellations (outside the canonical failure model)
  // and older API responses that don't project `renter_outcome`.
  switch (trip.status) {
    case "declined":
      return { sublineKey: "booking.sl_declined", ctaKey: "booking.cta_find_another_car", href: "/search", kind: "terminal" };
    case "expired":
      return { sublineKey: "booking.sl_expired", ctaKey: "booking.cta_find_another_car", href: "/search", kind: "terminal" };
    case "cancelled":
      return { sublineKey: "booking.sl_cancelled", ctaKey: "booking.cta_find_another_car", href: "/search", kind: "terminal" };
    default:
      return null;
  }
}
